import dayjs from 'dayjs/esm';
import {instantToPracticeWallDate} from '../../../services/practice-timezone.utils';

/**
 * Whether moving an occurrence from `originalStart` to `newStart` changes its **date** in
 * practice-local terms.
 *
 * A recurring occurrence may change its time of day but not its date. The server rejects
 * the latter: shifting dates re-schedules the whole tail — Monday lunches become Tuesday
 * lunches — while the stored rule still says "weekly on Monday", so the summary would
 * contradict the dates on screen.
 *
 * Both values are normalised to practice wall-clock before comparing, so the answer does
 * not depend on the viewer's device zone. That normalisation is the reason this lives here
 * rather than being hand-rolled at each call site: `originalStart` arrives as the zone-less
 * string the backend serves, while `newStart` is a true instant from the calendar grid, and
 * comparing the two directly is wrong for any out-of-zone viewer.
 *
 * Note this takes **raw** values. The appointment edit form compares picker values instead,
 * which are already practice wall-clock `Date`s on both sides — passing those through here
 * would convert a second time. See `recurringDateLockedValidator` for that path.
 */
export function movesToAnotherDay(
  originalStart: Date | string | number,
  newStart: Date | string | number,
  zone: string,
): boolean {
  const original = dayjs(instantToPracticeWallDate(originalStart, zone));
  const next = dayjs(instantToPracticeWallDate(newStart, zone));
  if (!original.isValid() || !next.isValid()) {
    return false;
  }
  return !next.isSame(original, 'day');
}

/**
 * The message shown when a cross-day move is refused. Shared so the drag path and the edit
 * form cannot drift from each other, and both stay aligned with the server's own wording.
 */
export const RECURRING_DATE_LOCKED_MESSAGE =
  'A repeating appointment\'s time can be changed, but not its date. To move it to another day, ' +
  'cancel this and the following appointments and create a new repeat instead.';
